import React, { useState, useEffect } from 'react';
import api from '../services/api';

const roleColors = { Admin: '#7c3aed', Cashier: '#0d9488', 'Meter Reader': '#ea580c' };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {mode:'add'} | {mode:'edit', user:{...}}

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get('/users'); setUsers(data); }
    catch (e) { alert('Error loading users'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>User Management</h2>
        <button className="btn btn-primary" onClick={() => setModal({ mode: 'add' })}>+ Add User</button>
      </div>

      {loading ? <p>Loading...</p> : (
        <div className="card">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px' }}>Name</th>
                <th style={{ padding: '12px 16px' }}>Username</th>
                <th style={{ padding: '12px 16px' }}>Role</th>
                <th style={{ padding: '12px 16px' }}>Status</th>
                <th style={{ padding: '12px 16px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600 }}>{u.name}</td>
                  <td style={{ padding: '12px 16px', color: '#666' }}>{u.username}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: `${roleColors[u.role] || '#888'}18`, color: roleColors[u.role] || '#888',
                      padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    }}>{u.role}</span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{
                      background: u.active ? '#dcfce7' : '#fef2f2', color: u.active ? '#16a34a' : '#dc2626',
                      padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                    }}>{u.active ? 'Active' : 'Inactive'}</span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <button className="btn btn-sm" onClick={() => setModal({ mode: 'edit', user: u })}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal && <UserModal {...modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function UserModal({ mode, user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user?.name || '', username: user?.username || '', password: '',
    role: user?.role || 'Cashier', active: user?.active ?? true,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.name || (!user && (!form.username || !form.password))) { setError('All fields required'); return; }
    setSaving(true); setError('');
    try {
      if (mode === 'add') {
        await api.post('/users', form);
      } else {
        const data = { name: form.name, role: form.role, active: form.active };
        if (form.password) data.password = form.password;
        await api.put(`/users/${user.id}`, data);
      }
      onSaved();
    } catch (e) { setError(e.response?.data?.message || 'Error saving user'); }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, padding: 28, width: 420, maxWidth: '90vw',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 20px' }}>{mode === 'add' ? 'Add User' : 'Edit User'}</h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Full Name</label>
            <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Username</label>
            <input value={form.username} onChange={e => setForm({...form, username: e.target.value})}
              disabled={mode === 'edit'}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', boxSizing: 'border-box',
                       background: mode === 'edit' ? '#f5f5f5' : '#fff' }} />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 13 }}>
              {mode === 'edit' ? 'New Password (leave blank to keep)' : 'Password'}
            </label>
            <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ fontWeight: 600, fontSize: 13 }}>Role</label>
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}
              style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', boxSizing: 'border-box' }}>
              <option>Admin</option>
              <option>Cashier</option>
              <option>Meter Reader</option>
            </select>
          </div>
          {mode === 'edit' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} />
              <span style={{ fontWeight: 600, fontSize: 13 }}>Active</span>
            </label>
          )}
        </div>

        {error && <div style={{ color: '#dc2626', background: '#fef2f2', borderRadius: 6, padding: '8px 12px', marginTop: 14, fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (mode === 'add' ? 'Create' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}
